import React from "react";
import { useRouteError, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { handleRole } from "./Helper/handleRoleHelper";
import AnimatedLoader from "./motionKit/AnimatedLoader";

// Router-level error boundary page. The warn composition (plane calmly
// detouring around an amber marker) keeps the tone reassuring, not alarming.
// UX-audit fixes: a 404 gets honest copy (not "unexpected error"), and the
// CTAs are real escape hatches (Dashboard / All Quotes) instead of a Reload
// that just replays the same broken URL.
export default function ErrorPage() {
  const error = useRouteError();
  const location = useLocation();
  const role = useSelector((s) => s?.reducer?.userLoggedIn?.role);
  // Only admin/operator/superAdmin have /dashboard + /all-quotes routes —
  // building them for user/agent/shipper roles would 404 straight back here.
  const hasWorkspace = ["admin", "superadmin", "operator"].includes(String(role || "").toLowerCase());
  const base = hasWorkspace ? `/${handleRole(role)}` : "";
  const notFound = error?.status === 404;
  const detail = error?.statusText || error?.message || "Unknown error";
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-[oklch(0.168_0.021_264)]">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)]">
        <div className="flex justify-center">
          <AnimatedLoader variant="warn" size={170} label={notFound ? "Page not found" : "Something went wrong"} />
        </div>
        <h1 className="mt-2 text-xl font-bold text-slate-900 dark:text-white">
          {notFound ? "This page doesn't exist" : "Something went off course"}
        </h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {notFound
            ? "The link may be outdated or the page was moved. Your data is safe."
            : "An unexpected error occurred. Your data is safe — try again, or head back to your work."}
        </p>
        {!notFound && (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-400 dark:bg-[oklch(0.168_0.021_264)] dark:text-slate-500">
            {String(detail).slice(0, 160)}
          </p>
        )}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <Link
            to={base ? `${base}/dashboard` : "/"}
            className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-500"
          >
            {base ? "Go to Dashboard" : "Go home"}
          </Link>
          {base ? (
            <Link
              to={`${base}/all-quotes`}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:border-[oklch(0.4_0.02_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
            >
              All Quotes
            </Link>
          ) : role ? null : (
            <Link
              to="/loginPage"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:border-[oklch(0.4_0.02_264)] dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Sign in
            </Link>
          )}
          {!notFound && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:border-[oklch(0.4_0.02_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
            >
              Retry
            </button>
          )}
        </div>
        {notFound && (
          <p className="mt-4 truncate font-mono text-[10.5px] text-slate-300 dark:text-slate-600">{location.pathname}</p>
        )}
      </div>
    </div>
  );
}
